/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import BoardIcon from '@atlaskit/icon/core/board';
import IconTile from '@atlaskit/icon/icon-tile';
import Image from '@atlaskit/image';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	JIRA_PLANS_UPDATE_WORK_SOURCE_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { JiraPlansUpdateWorkSourcePayload } from '@atlassian/conversation-assistant-message-actions-api/jira';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { EntityCard } from '../../common/ui/entity-card';
import { EntityTitle } from '../../common/ui/entity-title';
import { EntityValueGrid, EntityValueGridRow } from '../../common/ui/entity-value-grid';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';
import { jiraPlansActionData } from '../../common/utils/plan-action-data';

import messages from './messages';

const INTERACTION_SOURCE = 'jiraPlansUpdateWorkSourceRenderer';

const normalizeWorkSourceType = (type?: string): 'Project' | 'Board' => {
	const lowerCaseType = type?.toLowerCase();
	return lowerCaseType === 'space' || lowerCaseType === 'project' ? 'Project' : 'Board';
};

const styles = cssMap({
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: token('space.100'),
	},
});

export const filterUpdateWorkSourceActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansUpdateWorkSource]>[] => {
	const filteredActions = actions.filter((action) => assertIsUpdateWorkSourceAction(action));
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansUpdateWorkSource]
	>[];
};

const assertIsUpdateWorkSourceAction = (action: MessageAction): MessageAction => {
	if (
		!jiraPlansActionData.isPlanActionKeyForRenderer(action.key, JIRA_PLANS_UPDATE_WORK_SOURCE_KEY)
	) {
		throw new Error('Invalid action key for WorkSourceAction');
	}
	return action;
};

export const UpdateWorkSourceAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansUpdateWorkSource>,
): JSX.Element | null => {
	const { actions } = props;

	const firstAction = useMemo(() => filterUpdateWorkSourceActions(actions)[0], [actions]);
	if (!firstAction) {
		return null;
	}

	return <UpdateWorkSourceActionContent {...props} firstAction={firstAction} />;
};

const UpdateWorkSourceActionContent = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansUpdateWorkSource> & {
		firstAction: ActionWith<
			ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansUpdateWorkSource]
		>;
	},
) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const {
		getStatus,
		getReadonly,
		firstAction,
		onConfirm,
		onCancel,
		actions,
		sendMessageConfirmedTools,
		recipientAgentId,
		recipientAgentNamedId,
		conversationId,
		messageId,
	} = props;

	const isCustomAgent = !isDefaultRovoChat({
		agentId: recipientAgentId,
		agentNamedId: recipientAgentNamedId,
	});

	const { data } = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const invocationStatus = String(status ?? '').toLowerCase();
	const actionStatus = String(firstAction.status ?? '').toLowerCase();

	const isActionLoading = invocationStatus !== 'idle';
	const isSubmitting = invocationStatus === 'started';
	const isActionFinished = invocationStatus === 'finished' || actionStatus === 'finished';
	const buttonsDisabled = isActionLoading || readonly;

	const actionData = jiraPlansActionData.getPlanActionData<JiraPlansUpdateWorkSourcePayload>(data);

	const isRemove = actionData.deleteIssueSourceId !== undefined;

	const cardTitle = isRemove ? messages.removeCardTitle : messages.addCardTitle;
	const confirmButtonLabel = isRemove ? messages.removeButton : messages.addButton;

	const handleConfirm = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: `rovo-chat-jira-plans-work-source-${isRemove ? 'remove' : 'add'}-confirm`,
				workSourceId: actionData.projectId,
				planId: actionData.planId,
				planName: actionData.planName,
				workSourceName: actionData.title,
				type: actionData.type,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const argumentsOverride = isRemove
				? {
						deleteIssueSourceId: actionData.deleteIssueSourceId,
						planId: actionData.planId,
						planName: actionData.planName,
					}
				: {
						...actionData,
						type: normalizeWorkSourceType(actionData.type),
					};

			const confirmedData = jiraPlansActionData.withPlanActionData(
				firstAction.data,
				argumentsOverride,
			);

			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed', confirmedData)
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		if (isRemove) {
			const argumentsOverride = {
				deleteIssueSourceId: actionData.deleteIssueSourceId,
				planId: actionData.planId,
				planName: actionData.planName,
			};
			onConfirm({
				...firstAction,
				data: jiraPlansActionData.withPlanActionData(firstAction.data, argumentsOverride),
			});
		} else {
			const normalizedType =
				actionData.type != null &&
				(actionData.type.toLowerCase() === 'space' || actionData.type.toLowerCase() === 'project')
					? 'Project'
					: 'Board';
			const argumentsOverride = {
				...actionData,
				type: normalizedType,
			};
			onConfirm({
				...firstAction,
				data: jiraPlansActionData.withPlanActionData(firstAction.data, argumentsOverride),
			});
		}
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		isRemove,
		onConfirm,
		isCustomAgent,
	]);

	const handleCancel = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: `rovo-chat-jira-plans-work-source-${isRemove ? 'remove' : 'add'}-cancel`,
				workSourceId: actionData.projectId,
				planId: actionData.planId,
				planName: actionData.planName,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) => buildToolFromAction(action, 'cancelled')),
				},
			});
			return;
		}

		onCancel(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		isRemove,
		onCancel,
		isCustomAgent,
	]);

	const icon = useMemo(() => {
		const sourceType = actionData.type?.toLowerCase();

		// If the backend provides an avatar, prefer rendering it regardless of source type casing/value.
		if (actionData.avatarUrl) {
			return (
				<Tile label="" size="small" isInset={false}>
					<Image src={actionData.avatarUrl} alt="" />
				</Tile>
			);
		}

		if (sourceType?.includes('board')) {
			return <IconTile label="" appearance="gray" size="small" icon={BoardIcon} />;
		}

		if (sourceType?.includes('project') || sourceType?.includes('space')) {
			return <IconTile label="" appearance="gray" size="small" icon={SpacesIcon} />;
		}

		return null;
	}, [actionData.avatarUrl, actionData.type]);
	if (!actionData.planId) {
		return null;
	}
	return (
		<ActionCardWrapper isExpandable title={formatMessage(cardTitle)} product="plan">
			<EntityCard>
				<EntityTitle
					icon={icon}
					title={actionData.title ?? actionData.projectId ?? formatMessage(messages.workSource)}
				/>
				<EntityValueGrid>
					{actionData.type != null && (
						<EntityValueGridRow
							label={`${formatMessage(messages.sourceTypeLabel)}:`}
							value={actionData.type}
						/>
					)}
					{actionData.workItemCount != null && (
						<EntityValueGridRow
							label={`${formatMessage(messages.workItemsLabel)}:`}
							value={String(actionData.workItemCount)}
						/>
					)}
				</EntityValueGrid>
			</EntityCard>

			{!isActionFinished && (
				<ActionCardFooter>
					<Box xcss={styles.footer}>
						<Button appearance="default" onClick={handleCancel} isDisabled={buttonsDisabled}>
							{formatMessage(messages.cancelButton)}
						</Button>
						<Button
							appearance={isRemove ? 'warning' : 'primary'}
							onClick={handleConfirm}
							isDisabled={buttonsDisabled}
							isLoading={isSubmitting}
						>
							{formatMessage(confirmButtonLabel)}
						</Button>
					</Box>
				</ActionCardFooter>
			)}
		</ActionCardWrapper>
	);
};
